'use client';

import { Copy, Check } from 'lucide-react';
import { useState, useCallback, useEffect, useRef, type FocusEvent } from 'react';
import { dismissToast, useToast } from '@/hooks/use-toast';
import {
  Toast,
  ToastClose,
  ToastDescription,
  ToastProvider,
  ToastTitle,
  ToastViewport,
} from '@/components/ui/toast';

function CopyButton({ title, description }: { title?: string; description?: string }) {
  const [copied, setCopied] = useState(false);
  const handleCopy = useCallback(() => {
    const text = [title, typeof description === 'string' ? description : ''].filter(Boolean).join('\n');
    navigator.clipboard.writeText(text).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    });
  }, [title, description]);

  return (
    <button
      onClick={handleCopy}
      className="shrink-0 rounded-md p-1 opacity-70 hover:opacity-100 transition-opacity"
      aria-label="오류 메시지 복사"
    >
      {copied ? <Check className="h-3.5 w-3.5" /> : <Copy className="h-3.5 w-3.5" />}
    </button>
  );
}

/**
 * 닫히는 시간은 여기서 센다. Radix 의 duration 은 창이 포커스를 잃으면(window blur) 멈추는데,
 * 작성 화면은 편집기가 iframe 이라 문서를 누르는 순간 바깥 창이 blur 되어 알림이 영영 안 닫혔다(10-16 사람 신고).
 * 알림 위에 마우스가 있거나 키보드 포커스가 알림 안에 있는 동안은 멈추고, 풀리면 남은 시간을 이어 센다.
 */
function useAutoDismiss(id: string, ms: number, open: boolean | undefined) {
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const deadline = useRef(0);
  const remaining = useRef(ms);
  const hovered = useRef(false);
  const focused = useRef(false);

  const pause = useCallback(() => {
    if (!timer.current) return;
    clearTimeout(timer.current);
    timer.current = null;
    remaining.current = Math.max(0, deadline.current - Date.now());
  }, []);
  const resume = useCallback(() => {
    if (open === false || !Number.isFinite(ms) || hovered.current || focused.current || timer.current) return;
    deadline.current = Date.now() + remaining.current;
    timer.current = setTimeout(() => {
      timer.current = null;
      dismissToast(id);
    }, remaining.current);
  }, [id, ms, open]);

  // 새로 열리거나 시간이 바뀌면 처음부터 센다. 멈춘 동안이면 그대로 멈춰 있다.
  useEffect(() => {
    remaining.current = ms;
    resume();
    return () => {
      if (timer.current) clearTimeout(timer.current);
      timer.current = null;
    };
  }, [ms, resume]);

  return {
    onMouseEnter: () => { hovered.current = true; pause(); },
    onMouseLeave: () => { hovered.current = false; resume(); },
    onFocusCapture: () => { focused.current = true; pause(); },
    onBlurCapture: (event: FocusEvent<HTMLElement>) => {
      if (event.currentTarget.contains(event.relatedTarget as Node | null)) return;
      focused.current = false;
      resume();
    },
  };
}

type ToastItem = ReturnType<typeof useToast>['toasts'][number];

function ToastEntry({ toast: { id, title, description, action, duration, ...props } }: { toast: ToastItem }) {
  const isDestructive = props.variant === 'destructive';
  // 0 은 Radix 때처럼 기본 시간으로 본다(바로 닫지 않는다).
  const ms = duration || (isDestructive ? 5000 : 3000);
  const pauseOn = useAutoDismiss(id, ms, props.open);

  return (
    <Toast duration={Infinity} {...props} {...pauseOn}>
      <div className={`grid gap-1 ${isDestructive ? 'select-text' : ''}`}>
        {title && <ToastTitle>{title}</ToastTitle>}
        {description && (
          <ToastDescription className={isDestructive ? 'select-text cursor-text' : ''}>
            {description}
          </ToastDescription>
        )}
      </div>
      {isDestructive && (
        <CopyButton
          title={typeof title === 'string' ? title : undefined}
          description={typeof description === 'string' ? description : undefined}
        />
      )}
      {action}
      <ToastClose />
    </Toast>
  );
}

export function Toaster() {
  const { toasts } = useToast();

  return (
    <ToastProvider>
      {toasts.map((t) => (
        <ToastEntry key={t.id} toast={t} />
      ))}
      <ToastViewport />
    </ToastProvider>
  );
}
